import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ChevronDown, ChevronRight, Upload, FileText, Info, CheckCircle, XCircle, Mail, Activity, MessageSquare, Eye, Download } from "lucide-react";
import ComposeEmail from "./ComposeEmail";
import ActivityFeed from "./ActivityFeed";
import CommentSection from "./CommentSection";
import NotificationBell from "./NotificationBell";
import UserMenu from "./UserMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function AddFilesDropzone({ onDrop }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  return (
    <div
      {...getRootProps()}
      className={`file-upload-zone ${isDragActive ? "active" : ""}`}
      data-testid="add-files-dropzone"
    >
      <input {...getInputProps()} />
      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
      <p className="text-sm text-gray-600">
        {isDragActive ? "Drop files here" : "Drag & drop files, or click to select"}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        You can add multiple files one at a time
      </p>
    </div>
  );
}

function ProjectView({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [emailBasket, setEmailBasket] = useState([]);
  const [activeTab, setActiveTab] = useState("documents");
  
  // User permissions
  const [userPermissions, setUserPermissions] = useState([]);
  
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadDocType, setUploadDocType] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [suggestedVersions, setSuggestedVersions] = useState(null);
  const [versionSuggestionLoading, setVersionSuggestionLoading] = useState(false);
  
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectVersionId, setRejectVersionId] = useState("");
  const [rejectRemarks, setRejectRemarks] = useState("");

  const [showFileRejectDialog, setShowFileRejectDialog] = useState(false);
  const [rejectFileId, setRejectFileId] = useState("");
  const [rejectFileName, setRejectFileName] = useState("");
  const [fileRejectRemarks, setFileRejectRemarks] = useState("");

  const [showComposeEmail, setShowComposeEmail] = useState(false);

  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replaceFileId, setReplaceFileId] = useState("");
  const [replaceFileName, setReplaceFileName] = useState("");
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceComment, setReplaceComment] = useState("");

  const [showRevisionsDialog, setShowRevisionsDialog] = useState(false);
  const [fileRevisions, setFileRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);

  // Add files to existing version state
  const [showAddFilesDialog, setShowAddFilesDialog] = useState(false);
  const [addFilesVersionId, setAddFilesVersionId] = useState("");
  const [addFilesVersionLabel, setAddFilesVersionLabel] = useState("");
  const [addFilesFiles, setAddFilesFiles] = useState([]);
  const [addFilesUploading, setAddFilesUploading] = useState(false);
  const [addFilesProgress, setAddFilesProgress] = useState(0);

  // Upload progress state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Approval dialog state (for approval remarks)
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approveVersionId, setApproveVersionId] = useState("");
  const [approveRemarks, setApproveRemarks] = useState("");

  const [showFileApproveDialog, setShowFileApproveDialog] = useState(false);
  const [approveFileId, setApproveFileId] = useState("");
  const [approveFileName, setApproveFileName] = useState("");
  const [fileApproveRemarks, setFileApproveRemarks] = useState("");

  useEffect(() => {
    fetchData();
    fetchUserPermissions();
  }, [projectId]);
  
  const fetchUserPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions?project_id=${projectId}`, getAuthHeaders());
      setUserPermissions(res.data.effective_permissions || []);
    } catch (error) {
      console.error("Failed to load user permissions:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [projectRes, versionsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, getAuthHeaders()),
        axios.get(`${API}/projects/${projectId}/versions`, getAuthHeaders()),
      ]);
      setProject(projectRes.data);
      setVersions(versionsRes.data);
    } catch (error) {
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    // Append new files to existing files instead of replacing
    setUploadFiles(prev => {
      // Filter out duplicates by file name
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
  };

  // Function to remove a file from the upload list
  const removeUploadFile = (fileName) => {
    setUploadFiles(prev => prev.filter(f => f.name !== fileName));
  };

  // Clear all upload files
  const clearUploadFiles = () => {
    setUploadFiles([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const fetchVersionSuggestion = async (docType) => {
    if (!docType) return;
    
    setVersionSuggestionLoading(true);
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/suggest-version/${docType}`,
        getAuthHeaders()
      );
      setSuggestedVersions(response.data);
      // Auto-fill with default suggested version
      setUploadVersion(response.data.suggested_versions.default);
    } catch (error) {
      console.error("Failed to fetch version suggestion:", error);
      // Fallback to 1.0
      setUploadVersion("1.0");
      setSuggestedVersions({
        current_version: null,
        suggested_versions: { default: "1.0", minor: "1.0", major: "1.0" }
      });
    } finally {
      setVersionSuggestionLoading(false);
    }
  };

  const handleDocTypeChange = (docType) => {
    setUploadDocType(docType);
    fetchVersionSuggestion(docType);
  };

  const handleUploadVersion = async (e) => {
    e.preventDefault();
    
    if (uploadFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("doc_type", uploadDocType);
    formData.append("version", uploadVersion);
    uploadFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setUploading(true);
      setUploadProgress(0);
      
      await axios.post(`${API}/versions/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      toast.success("Version uploaded successfully");
      setShowUploadDialog(false);
      setUploadVersion("");
      setUploadDocType("");
      setUploadFiles([]);
      setSuggestedVersions(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Add files to existing version
  const openAddFilesDialog = (versionId, docType, version) => {
    setAddFilesVersionId(versionId);
    setAddFilesVersionLabel(`${docType} v${version}`);
    setAddFilesFiles([]);
    setShowAddFilesDialog(true);
  };

  const onDropAddFiles = (acceptedFiles) => {
    setAddFilesFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
  };

  const removeAddFile = (fileName) => {
    setAddFilesFiles(prev => prev.filter(f => f.name !== fileName));
  };

  const handleAddFiles = async (e) => {
    e.preventDefault();
    if (addFilesFiles.length === 0) {
      toast.error("Please select files to add");
      return;
    }

    const formData = new FormData();
    formData.append("version_group_id", addFilesVersionId);
    addFilesFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setAddFilesUploading(true);
      setAddFilesProgress(0);

      await axios.post(`${API}/versions/add-files`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setAddFilesProgress(percentCompleted);
        },
      });
      toast.success("Files added successfully");
      setShowAddFilesDialog(false);
      setAddFilesFiles([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add files");
    } finally {
      setAddFilesUploading(false);
      setAddFilesProgress(0);
    }
  };

  // Open approval dialog for version-level approval
  const openApproveVersionDialog = (versionGroupId) => {
    setApproveVersionId(versionGroupId);
    setApproveRemarks("");
    setShowApproveDialog(true);
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/versions/approval`,
        { version_group_id: approveVersionId, action: "approve", remarks: approveRemarks || null },
        getAuthHeaders()
      );
      toast.success("Version approved successfully");
      setShowApproveDialog(false);
      setApproveVersionId("");
      setApproveRemarks("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Approval failed");
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/versions/approval`,
        {
          version_group_id: rejectVersionId,
          action: "reject",
          remarks: rejectRemarks,
        },
        getAuthHeaders()
      );
      toast.success("Version rejected");
      setShowRejectDialog(false);
      setRejectVersionId("");
      setRejectRemarks("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rejection failed");
    }
  };

  // Open approval dialog for file-level approval
  const openFileApproveDialog = (fileId, fileName) => {
    setApproveFileId(fileId);
    setApproveFileName(fileName);
    setFileApproveRemarks("");
    setShowFileApproveDialog(true);
  };

  const handleFileApprove = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/files/approval`,
        { file_id: approveFileId, action: "approve", remarks: fileApproveRemarks || null },
        getAuthHeaders()
      );
      toast.success("File approved successfully");
      setShowFileApproveDialog(false);
      setApproveFileId("");
      setApproveFileName("");
      setFileApproveRemarks("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "File approval failed");
    }
  };

  const handleFileReject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/files/approval`,
        {
          file_id: rejectFileId,
          action: "reject",
          remarks: fileRejectRemarks,
        },
        getAuthHeaders()
      );
      toast.success("File rejected");
      setShowFileRejectDialog(false);
      setRejectFileId("");
      setRejectFileName("");
      setFileRejectRemarks("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "File rejection failed");
    }
  };

  const openFileRejectDialog = (fileId, fileName) => {
    setRejectFileId(fileId);
    setRejectFileName(fileName);
    setShowFileRejectDialog(true);
  };

  const handleReplaceFile = async (e) => {
    e.preventDefault();
    
    if (!replaceFile) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file_id", replaceFileId);
    formData.append("file", replaceFile);
    if (replaceComment.trim()) {
      formData.append("replace_comment", replaceComment.trim());
    }

    try {
      await axios.post(`${API}/versions/replace-file`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("File replaced successfully - new revision created");
      setShowReplaceDialog(false);
      setReplaceFileId("");
      setReplaceFileName("");
      setReplaceFile(null);
      setReplaceComment("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "File replacement failed");
    }
  };

  const openReplaceDialog = (fileId, fileName) => {
    setReplaceFileId(fileId);
    setReplaceFileName(fileName);
    setReplaceFile(null);
    setReplaceComment("");
    setShowReplaceDialog(true);
  };

  const viewRevisionHistory = async (fileId) => {
    setRevisionsLoading(true);
    setShowRevisionsDialog(true);
    try {
      const response = await axios.get(
        `${API}/files/${fileId}/revisions`,
        getAuthHeaders()
      );
      setFileRevisions(response.data);
    } catch (error) {
      toast.error("Failed to load revision history");
      setFileRevisions([]);
    } finally {
      setRevisionsLoading(false);
    }
  };

  const downloadFile = async (fileId, revision = null) => {
    try {
      toast.info('Starting download...');
      
      const token = localStorage.getItem("token");
      
      // First, try to get a presigned URL (for R2 files - better for large files)
      try {
        let presignedUrl = `${API}/files/presigned-url/${fileId}`;
        if (revision) {
          presignedUrl += `?revision=${revision}`;
        }
        
        const presignedResponse = await fetch(presignedUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (presignedResponse.ok) {
          const data = await presignedResponse.json();
          if (data.url && !data.note) {
            // R2 presigned URL - use direct download
            console.log('Using presigned URL for direct download');
            window.open(data.url, '_blank');
            toast.success('Download started (direct)');
            return;
          }
        }
      } catch (presignedError) {
        console.log('Presigned URL not available, falling back to standard download');
      }
      
      // Fallback: Use standard download endpoint
      let downloadUrl;
      if (revision) {
        downloadUrl = `${API}/files/download/${fileId}?revision=${revision}&token=${token}`;
      } else {
        downloadUrl = `${API}/files/download/${fileId}?token=${token}`;
      }
      
      console.log('Downloading from:', downloadUrl);
      
      // Use direct link approach for better compatibility with large files
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      console.log('✅ Download triggered');
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 1000);
      
      toast.success('Download started');
    } catch (error) {
      console.error('❌ DOWNLOAD ERROR:', error);
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const viewFile = async (fileId) => {
    try {
      const token = localStorage.getItem("token");
      
      // First, try to get a presigned URL (for R2 files - better for large files)
      try {
        const presignedUrl = `${API}/files/presigned-url/${fileId}`;
        const presignedResponse = await fetch(presignedUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (presignedResponse.ok) {
          const data = await presignedResponse.json();
          if (data.url && !data.note) {
            // R2 presigned URL - open directly
            window.open(data.url, '_blank');
            return;
          }
        }
      } catch (presignedError) {
        console.log('Presigned URL not available, falling back to standard view');
      }
      
      // Fallback: Use standard view endpoint
      const viewUrl = `${API}/files/download/${fileId}?token=${token}&inline=true`;
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('View file error:', error);
      toast.error('Failed to open file');
    }
  };

  const toggleVersion = (versionId) => {
    setExpandedVersions((prev) => ({
      ...prev,
      [versionId]: !prev[versionId],
    }));
  };

  const addToEmailBasket = (file, version, docType) => {
    const item = { file, version, docType };
    setEmailBasket((prev) => [...prev, item]);
    toast.success("Added to email basket");
  };

  const handleAskAI = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    setAiResponse("");

    try {
      const response = await axios.post(
        `${API}/project-assistant`,
        null,
        {
          ...getAuthHeaders(),
          params: { project_id: projectId, query: aiQuery },
        }
      );
      setAiResponse(response.data.response);
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI query failed");
    } finally {
      setAiLoading(false);
    }
  };

  // Permission check helper
  const hasPermission = (perm) => userPermissions.includes(perm);
  
  // Check permissions using RBAC or fallback to legacy roles
  const canWrite = hasPermission("document.create") || ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role);
  const canApprove = hasPermission("document.approve") || user.role === "APPROVER" || user.role === "ADMIN";
  const canReject = hasPermission("document.reject") || user.role === "APPROVER" || user.role === "ADMIN";
  const canReplace = hasPermission("document.replace") || ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role);
  const canComment = hasPermission("document.comment") || true; // Allow all users to comment by default
  const canComposeEmail = hasPermission("email.compose") || ["ADMIN", "PROJECT_WRITE", "APPROVER"].includes(user.role);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div data-testid="project-view-page">
      <Toaster position="top-right" richColors />
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{project?.name}</h1>
            <p className="text-blue-100 text-sm mt-1">Project ID: {projectId}</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <Button
              onClick={() => setShowComposeEmail(true)}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              data-testid="compose-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Compose Email
            </Button>
            <Button
              onClick={() => navigate(`/project/${projectId}/emails`)}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              data-testid="view-emails-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              View Emails
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="erp-container">
        <div className="mb-6 flex gap-3">
          {canWrite && (
            <Dialog open={showUploadDialog} onOpenChange={(open) => {
              setShowUploadDialog(open);
              if (!open) {
                // Clear form when dialog is closed
                setUploadFiles([]);
                setUploadDocType("");
                setUploadVersion("");
                setSuggestedVersions(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button className="erp-btn erp-btn-primary" data-testid="upload-version-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New Version
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Upload New Version</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUploadVersion}>
                  <div className="mb-4">
                    <Label>Document Type</Label>
                    <Select value={uploadDocType} onValueChange={handleDocTypeChange} required>
                      <SelectTrigger data-testid="doc-type-select">
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Client Docs">Client Docs</SelectItem>
                        <SelectItem value="Design">Design</SelectItem>
                        <SelectItem value="Mezzanine">Mezzanine</SelectItem>
                        <SelectItem value="BOM">BOM</SelectItem>
                        <SelectItem value="Quotation">Quotation</SelectItem>
                        <SelectItem value="Purchase Order">Purchase Order</SelectItem>
                        <SelectItem value="Invoice">Invoice</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <Label>Version (e.g., 1.0, 2.1)</Label>
                    {versionSuggestionLoading ? (
                      <div className="text-sm text-gray-500 py-2">Loading version suggestions...</div>
                    ) : suggestedVersions && (
                      <div className="mb-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        {suggestedVersions.current_version ? (
                          <div className="text-sm">
                            <p className="text-gray-700 mb-2">
                              <strong>Current version:</strong> {suggestedVersions.current_version}
                            </p>
                            <p className="text-gray-700">
                              <strong>Quick select:</strong>{" "}
                              <button
                                type="button"
                                onClick={() => setUploadVersion(suggestedVersions.suggested_versions.minor)}
                                className="text-blue-600 hover:underline mx-2"
                                data-testid="version-minor-suggestion"
                              >
                                {suggestedVersions.suggested_versions.minor} (minor)
                              </button>
                              |
                              <button
                                type="button"
                                onClick={() => setUploadVersion(suggestedVersions.suggested_versions.major)}
                                className="text-blue-600 hover:underline mx-2"
                                data-testid="version-major-suggestion"
                              >
                                {suggestedVersions.suggested_versions.major} (major)
                              </button>
                            </p>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-700">
                            <strong>No previous versions.</strong> Starting with version 1.0
                          </div>
                        )}
                      </div>
                    )}
                    <Input
                      value={uploadVersion}
                      onChange={(e) => setUploadVersion(e.target.value)}
                      placeholder="1.0"
                      required
                      data-testid="version-input"
                    />
                  </div>
                  <div className="mb-4">
                    <Label>Files</Label>
                    <div
                      {...getRootProps()}
                      className={`file-upload-zone ${isDragActive ? "active" : ""}`}
                      data-testid="file-dropzone"
                    >
                      <input {...getInputProps()} />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {isDragActive
                          ? "Drop files here"
                          : "Drag & drop files, or click to select"}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        You can add multiple files one at a time
                      </p>
                    </div>
                  </div>
                  
                  {/* Selected Files List */}
                  {uploadFiles.length > 0 && (
                    <div className="mb-4 border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {uploadFiles.length} file(s) selected
                        </span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={clearUploadFiles}
                          className="text-xs text-red-600 hover:text-red-700 h-6 px-2"
                        >
                          Clear All
                        </Button>
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {uploadFiles.map((file, index) => (
                          <div 
                            key={`${file.name}-${index}`} 
                            className="grid grid-cols-[1fr_auto_auto] items-center py-1 px-2 bg-white rounded border text-sm gap-2"
                          >
                            <span 
                              className="text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap" 
                              title={file.name}
                              style={{ maxWidth: '300px' }}
                            >
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                            <button
                              type="button"
                              onClick={() => removeUploadFile(file.name)}
                              className="text-red-500 hover:text-red-700 text-sm font-bold w-5 h-5 flex items-center justify-center rounded hover:bg-red-50"
                              title="Remove file"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Progress */}
                  {uploading && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-2" />
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={uploading}
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? `Uploading... ${uploadProgress}%` : "Upload Version"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="ai-assistant-btn">
                <FileText className="w-4 h-4 mr-2" />
                AI Project Assistant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>AI Project Assistant</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAskAI}>
                <div className="mb-4">
                  <Label>Ask about this project</Label>
                  <Textarea
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    placeholder="What documents are approved?"
                    rows={3}
                    required
                    data-testid="ai-query-input"
                  />
                </div>
                <Button type="submit" className="w-full mb-4" disabled={aiLoading} data-testid="submit-ai-query-btn">
                  {aiLoading ? "Asking AI..." : "Ask AI"}
                </Button>
                {aiResponse && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="ai-response">
                    <p className="text-sm text-gray-800">{aiResponse}</p>
                  </div>
                )}
              </form>
            </DialogContent>
          </Dialog>

          {emailBasket.length > 0 && (
            <Button variant="outline" data-testid="email-basket-btn">
              Email Basket ({emailBasket.length})
            </Button>
          )}
        </div>

        {/* Tabbed Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="documents-tab">
              <FileText className="w-4 h-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="activity-tab">
              <Activity className="w-4 h-4" />
              Activity & Comments
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="erp-card">
              <h2 className="text-xl font-bold mb-4">Document Versions</h2>
              {versions.length === 0 ? (
                <p className="text-gray-500" data-testid="no-versions-message">No versions uploaded yet.</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((version) => (
                    <Collapsible
                      key={version.id}
                      open={expandedVersions[version.id]}
                      onOpenChange={() => toggleVersion(version.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <div
                          className="version-row p-4 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-50"
                          data-testid={`version-row-${version.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {expandedVersions[version.id] ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                            <div>
                              <div className="font-semibold">
                                {version.doc_type} - Version {version.version}
                              </div>
                              <div className="text-sm text-gray-600">
                                {new Date(version.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                              </div>
                              {/* File status counts - color coded */}
                              <div className="flex items-center gap-2 mt-1 text-xs">
                                <span>Files:</span>
                                {version.approved_count > 0 && (
                                  <span className="text-green-600 font-medium">{version.approved_count} Approved</span>
                                )}
                                {version.approved_count > 0 && (version.rejected_count > 0 || version.draft_count > 0) && <span className="text-gray-400">|</span>}
                                {version.rejected_count > 0 && (
                                  <span className="text-red-600 font-medium">{version.rejected_count} Rejected</span>
                                )}
                                {version.rejected_count > 0 && version.draft_count > 0 && <span className="text-gray-400">|</span>}
                                {version.draft_count > 0 && (
                                  <span className="text-yellow-600 font-medium">{version.draft_count} Draft</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`status-badge status-${version.status.toLowerCase().replace(' ', '-')}`}>
                              {version.status}
                            </span>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="expandable-content p-4 mt-2 rounded-lg">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-sm">
                                <th className="pb-2">File Name</th>
                                <th className="pb-2">Revision</th>
                                <th className="pb-2">Status</th>
                                <th className="pb-2">Uploaded By</th>
                                <th className="pb-2">Date</th>
                                <th className="pb-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {version.files.map((file) => (
                                <tr key={file.id} className="text-sm border-t" data-testid={`file-row-${file.id}`}>
                                  <td className="py-2">{file.file_name}</td>
                                  <td className="py-2">Rev {file.revision_number}</td>
                                  <td className="py-2">
                                    <span className={`status-badge status-${(file.status || 'draft').toLowerCase()}`}>
                                      {file.status || 'Draft'}
                                    </span>
                                  </td>
                                  <td className="py-2">{file.uploaded_by}</td>
                                  <td className="py-2">
                                    {new Date(file.uploaded_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
                                  </td>
                                  <td className="py-2">
                                    <div className="flex gap-2 flex-wrap">
                                      {/* Individual file approval buttons - show only for Draft files when user is approver */}
                                      {(file.status === "Draft" || !file.status) && canApprove && version.status !== "Superseded" && (
                                        <>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-green-600 border-green-600 hover:bg-green-50"
                                            onClick={() => openFileApproveDialog(file.id, file.file_name)}
                                            data-testid={`approve-file-${file.id}`}
                                          >
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            Approve
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-red-600 border-red-600 hover:bg-red-50"
                                            onClick={() => openFileRejectDialog(file.id, file.file_name)}
                                            data-testid={`reject-file-${file.id}`}
                                          >
                                            <XCircle className="w-3 h-3 mr-1" />
                                            Reject
                                          </Button>
                                        </>
                                      )}
                                      {version.status === "Approved" && file.status === "Approved" && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => addToEmailBasket(file, version.version, version.doc_type)}
                                          data-testid={`add-to-email-${file.id}`}
                                        >
                                          Add to Email
                                        </Button>
                                      )}
                                      {/* Replace button - show for non-superseded versions, allows replacing Draft/Approved/Rejected files */}
                                      {version.status !== "Superseded" && canReplace && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => openReplaceDialog(file.id, file.file_name)}
                                          data-testid={`replace-file-${file.id}`}
                                        >
                                          Replace File
                                        </Button>
                                      )}
                                      {/* View button - opens file in new browser tab */}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => viewFile(file.id)}
                                        data-testid={`view-file-${file.id}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => downloadFile(file.id)}
                                        data-testid={`download-file-${file.id}`}
                                      >
                                        <Download className="w-3 h-3 mr-1" />
                                        Download
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => viewRevisionHistory(file.id)}
                                        data-testid={`view-revisions-${file.id}`}
                                      >
                                        History
                                      </Button>
                                    </div>
                                    {file.status === "Rejected" && file.rejected_remarks && (
                                      <div className="mt-1 text-xs text-red-600">
                                        Rejected: {file.rejected_remarks}
                                      </div>
                                    )}
                                    {file.status === "Approved" && file.approved_remarks && (
                                      <div className="mt-1 text-xs text-green-600">
                                        Approved: {file.approved_remarks}
                                      </div>
                                    )}
                                    {file.replace_comment && (
                                      <div className="mt-1 text-xs text-blue-600" data-testid={`replace-comment-${file.id}`}>
                                        Replaced: {file.replace_comment}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {/* Add Files button for non-superseded versions */}
                          {version.status !== "Superseded" && canWrite && (
                            <div className="mt-3 mb-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                onClick={() => openAddFilesDialog(version.id, version.doc_type, version.version)}
                                data-testid={`add-files-btn-${version.id}`}
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Add Files to This Version
                              </Button>
                            </div>
                          )}

                          {/* Version-level approval bar - show only when there are Draft files */}
                          {version.draft_count > 0 && version.status !== "Superseded" && canApprove && (
                            <div className="approval-action-bar">
                              <div className="info-section">
                                <Info className="info-icon" />
                                <div>
                                  <span className="info-text">{version.draft_count} Draft file(s) awaiting approval</span>
                                  <span className="info-role">• For: Approver • Use buttons below to approve/reject Draft files only</span>
                                </div>
                              </div>
                              <div className="actions">
                                <button
                                  onClick={() => openApproveVersionDialog(version.id)}
                                  className="btn-approve"
                                  data-testid={`approve-all-btn-${version.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Approve All Drafts
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectVersionId(version.id);
                                    setShowRejectDialog(true);
                                  }}
                                  className="btn-reject"
                                  data-testid={`reject-all-btn-${version.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                  Reject All Drafts
                                </button>
                              </div>
                            </div>
                          )}

                          {version.rejected_remarks && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded" data-testid="rejection-remarks">
                              <p className="text-sm text-red-900">
                                <strong>Rejection Remarks:</strong> {version.rejected_remarks}
                              </p>
                            </div>
                          )}

                          {version.approved_remarks && (
                            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded" data-testid="approval-remarks">
                              <p className="text-sm text-green-900">
                                <strong>Approval Remarks:</strong> {version.approved_remarks}
                              </p>
                            </div>
                          )}

                          {/* File Comments Section */}
                          <div className="mt-4 pt-4 border-t">
                            <CommentSection
                              projectId={projectId}
                              targetType="version"
                              targetId={version.id}
                              targetName={`${version.doc_type} v${version.version}`}
                              user={user}
                            />
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Activity & Comments Tab */}
          <TabsContent value="activity">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Activity Feed */}
              <div className="erp-card">
                <ActivityFeed projectId={projectId} projectName={project?.name} />
              </div>

              {/* Project-Level Comments */}
              <div className="erp-card">
                <CommentSection
                  projectId={projectId}
                  targetType="project"
                  targetId={projectId}
                  targetName="Project Discussion"
                  user={user}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Version</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReject}>
            <div className="mb-4">
              <Label>Rejection Remarks</Label>
              <Textarea
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                placeholder="Provide reason for rejection"
                rows={4}
                required
                data-testid="reject-remarks-input"
              />
            </div>
            <Button type="submit" className="w-full erp-btn erp-btn-danger" data-testid="submit-reject-btn">
              Confirm Rejection
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Version Approval Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Version</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApprove}>
            <div className="mb-4">
              <Label>Approval Remarks (Optional)</Label>
              <Textarea
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
                placeholder="Add any comments or notes about this approval"
                rows={4}
                data-testid="approve-remarks-input"
              />
            </div>
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="submit-approve-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Approval
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* File Approval Dialog */}
      <Dialog open={showFileApproveDialog} onOpenChange={setShowFileApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileApprove}>
            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-700">
                <strong>File:</strong> {approveFileName}
              </p>
            </div>
            <div className="mb-4">
              <Label>Approval Remarks (Optional)</Label>
              <Textarea
                value={fileApproveRemarks}
                onChange={(e) => setFileApproveRemarks(e.target.value)}
                placeholder="Add any comments about this file approval"
                rows={3}
                data-testid="file-approve-remarks-input"
              />
            </div>
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="submit-file-approve-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve File
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace File (New Revision)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReplaceFile}>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Current file:</strong> {replaceFileName}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                This will create a new revision and mark the previous revision as inactive.
              </p>
            </div>
            <div className="mb-4">
              <Label>Select New File</Label>
              <Input
                type="file"
                onChange={(e) => setReplaceFile(e.target.files[0])}
                required
                data-testid="replace-file-input"
              />
            </div>
            <div className="mb-4">
              <Label>Reason for Replacement (Optional)</Label>
              <Textarea
                value={replaceComment}
                onChange={(e) => setReplaceComment(e.target.value)}
                placeholder="Explain why this file is being replaced..."
                className="mt-1"
                rows={3}
                data-testid="replace-comment-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="submit-replace-btn">
              Replace File
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={showRevisionsDialog} onOpenChange={setShowRevisionsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Revision History</DialogTitle>
          </DialogHeader>
          {revisionsLoading ? (
            <div className="py-8 text-center text-gray-500">Loading revision history...</div>
          ) : fileRevisions.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No revisions found</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-sm">
                    <th className="p-3">Revision</th>
                    <th className="p-3">File Name</th>
                    <th className="p-3">Uploaded By</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fileRevisions.map((rev) => (
                    <tr key={`${rev.id}-${rev.revision_number}`} className="border-t text-sm">
                      <td className="p-3">
                        <span className="font-semibold">Rev {rev.revision_number}</span>
                        {rev.is_active && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Current
                          </span>
                        )}
                        {rev.replace_comment && (
                          <div className="text-xs text-blue-600 mt-1" data-testid={`rev-comment-${rev.revision_number}`}>
                            {rev.replace_comment}
                          </div>
                        )}
                      </td>
                      <td className="p-3">{rev.file_name}</td>
                      <td className="p-3">{rev.uploaded_by}</td>
                      <td className="p-3">{new Date(rev.uploaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                      <td className="p-3">
                        {rev.is_active ? (
                          <span className="text-green-600">Active</span>
                        ) : (
                          <span className="text-gray-500">Superseded</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadFile(rev.id, rev.revision_number)}
                          data-testid={`download-rev-${rev.revision_number}`}
                        >
                          Download
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* File Rejection Dialog */}
      <Dialog open={showFileRejectDialog} onOpenChange={setShowFileRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileReject}>
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-gray-700">
                <strong>File:</strong> {rejectFileName}
              </p>
            </div>
            <div className="mb-4">
              <Label>Rejection Remarks</Label>
              <Textarea
                value={fileRejectRemarks}
                onChange={(e) => setFileRejectRemarks(e.target.value)}
                placeholder="Provide reason for rejecting this file"
                rows={4}
                required
                data-testid="file-reject-remarks-input"
              />
            </div>
            <Button type="submit" className="w-full erp-btn erp-btn-danger" data-testid="submit-file-reject-btn">
              Confirm File Rejection
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Files to Existing Version Dialog */}
      <Dialog open={showAddFilesDialog} onOpenChange={(open) => {
        setShowAddFilesDialog(open);
        if (!open) setAddFilesFiles([]);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Files to {addFilesVersionLabel}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFiles}>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                New files will be added to <strong>{addFilesVersionLabel}</strong> with <strong>Draft</strong> status. Existing files in this version are not affected.
              </p>
            </div>
            <div className="mb-4">
              <Label>Files</Label>
              <AddFilesDropzone onDrop={onDropAddFiles} />
            </div>

            {addFilesFiles.length > 0 && (
              <div className="mb-4 border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {addFilesFiles.length} file(s) selected
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAddFilesFiles([])}
                    className="text-xs text-red-600 hover:text-red-700 h-6 px-2"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {addFilesFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="grid grid-cols-[1fr_auto_auto] items-center py-1 px-2 bg-white rounded border text-sm gap-2"
                    >
                      <span
                        className="text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap"
                        title={file.name}
                        style={{ maxWidth: '300px' }}
                      >
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAddFile(file.name)}
                        className="text-red-500 hover:text-red-700 text-sm font-bold w-5 h-5 flex items-center justify-center rounded hover:bg-red-50"
                        title="Remove file"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {addFilesUploading && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{addFilesProgress}%</span>
                </div>
                <Progress value={addFilesProgress} className="h-2" />
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={addFilesUploading || addFilesFiles.length === 0}
              data-testid="submit-add-files-btn"
            >
              {addFilesUploading ? `Uploading... ${addFilesProgress}%` : `Add ${addFilesFiles.length} File(s)`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <ComposeEmail
        open={showComposeEmail}
        onOpenChange={setShowComposeEmail}
        projectId={projectId}
        projectName={project?.name || ""}
        availableFiles={versions.flatMap(v => v.files || [])}
        onEmailSent={() => {
          toast.success("Email sent and linked to project!");
        }}
      />
    </div>
  );
}

export default ProjectView;
